import React, { useMemo, useState } from 'react';
import { api } from '../../../lib/api';
import Button from '../../../components/ui/Button';

type ExportFormat = 'pdf' | 'xlsx' | 'csv';

function extForFormat(fmt: ExportFormat): string {
  if (fmt === 'xlsx') return 'xlsx';
  if (fmt === 'csv') return 'csv';
  return 'pdf';
}

export default function CatalogExportModal({
  open,
  onClose,
  classroomId,
  configId,
  periodIndex,
  classLabel,
  schoolYearLabel,
}: {
  open: boolean;
  onClose: () => void;
  classroomId: number;
  configId: number;
  periodIndex: number | null;
  classLabel?: string | null;
  schoolYearLabel?: string | null;
}) {
  const [format, setFormat] = useState<ExportFormat>('pdf');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const label = useMemo(() => {
    const cls = String(classLabel || 'clasa').replace(/\s+/g, '_');
    const year = String(schoolYearLabel || 'catalog').replace(/\s+/g, '_');
    return `catalog_${cls}_${year}.${extForFormat(format)}`;
  }, [classLabel, format, schoolYearLabel]);

  const download = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await api.get(`/api/catalog/classroom/${classroomId}/export`, {
        params: {
          config_id: configId,
          period_index: periodIndex == null ? undefined : periodIndex,
          format,
        },
        responseType: 'blob',
      });

      const cd = String(res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'] || '');
      const match = /filename=\"?([^\";]+)\"?/i.exec(cd);
      const filename = (match?.[1] || '').trim() || label;

      const blob = new Blob([res.data], { type: res.data?.type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Nu s-a putut exporta catalogul.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-lg border border-border bg-background p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-base font-semibold text-foreground">Export catalog (clasă)</div>
            <div className="text-xs text-muted-foreground mt-1">
              PDF / Excel (XLSX) / CSV. Perioadă: {periodIndex == null ? 'toate' : periodIndex}
            </div>
          </div>
          <button type="button" className="text-muted-foreground hover:text-foreground" onClick={onClose} aria-label="Închide">
            ✕
          </button>
        </div>

        <div className="mt-4">
          <div className="text-xs text-muted-foreground mb-2">Format</div>
          <div className="flex flex-wrap gap-2">
            {(['pdf', 'xlsx', 'csv'] as ExportFormat[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFormat(f)}
                className={`px-3 py-1.5 rounded-md text-sm border transition-colors duration-150 ${
                  format === f ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border hover:bg-muted'
                }`}
              >
                {f.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {error ? <div className="mt-4 bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{error}</div> : null}

        <div className="mt-5 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Renunță
          </Button>
          <Button iconName="FileDown" onClick={() => void download()} loading={loading}>
            Exportă
          </Button>
        </div>
      </div>
    </div>
  );
}

